
import { Order, Driver, SystemConfig, FinancialSnapshot } from '../types';

// --- PAYMENT HELPERS ---

export const isPaymentCash = (method: string) => {
    const m = method.toLowerCase();
    return m.includes('dinheiro') || m.includes('espécie');
};

export const isPaymentOnline = (method: string) => {
    const m = method.toLowerCase();

    // Se a string explicitamente disser que é para cobrar, nunca é online.
    if (m.includes('cobrar') || m.includes('na porta') || m.includes('na entrega')) {
        return false;
    }

    // Padrão Open Delivery: Prepaid = true
    // Padrão Site Próprio: Automático ou Online
    return m.includes('pago') ||
        m.includes('online') ||
        m.includes('ifood') ||
        m.includes('pix (automático)') ||
        m.includes('automático') ||
        m.includes('rappi');
};

export const isPaymentCard = (method: string) => {
    const m = method.toLowerCase();
    // Se for card mas não for online, assume que é Maquininha (Cobrar)
    return (m.includes('maquin') || m.includes('card') || m.includes('cartão') || m.includes('débito') || m.includes('crédito'))
        && !m.includes('online') && !m.includes('site');
};

// --- ACCOUNTING LOGIC (HISTORICAL/REAL) ---

export const calculateFinancials = (
    orders: Order[],
    drivers: Driver[],
    config: SystemConfig
): FinancialSnapshot => {

    let revenueOnline = 0;
    let revenueCash = 0;
    let revenueCard = 0;
    let costIncidents = 0;

    // v7.9.3: Contabilidade Granular
    let grossProducts = 0;
    let grossFees = 0;

    const validOrders = orders.filter(o => o.status === 'delivered' || o.status === 'archived');

    validOrders.forEach(o => {
        let orderVal = o.orderValue || 0;
        const fee = o.deliveryFee || 0;

        // Separação Produto vs Taxa para Auditoria
        // Assumimos que orderValue é o TOTAL (Produto + Taxa).
        // Se houver desconto (incidentes), impacta o total arrecadado.

        if (o.incidents) {
            o.incidents.forEach(inc => {
                costIncidents += Math.abs(inc.valueImpact);
                if (inc.valueImpact < 0) {
                    orderVal += inc.valueImpact;
                }
            });
        }
        orderVal = Math.max(0, orderVal);

        // Contabiliza totais por método
        if (isPaymentOnline(o.paymentMethod)) revenueOnline += orderVal;
        else if (isPaymentCash(o.paymentMethod)) revenueCash += orderVal;
        else revenueCard += orderVal;

        // Contabiliza breakdown
        // Se o valor total foi reduzido abaixo da taxa (ex: desconto total), ajustamos
        const effectiveFee = Math.min(orderVal, fee);
        const effectiveProduct = Math.max(0, orderVal - effectiveFee);

        grossFees += effectiveFee;
        grossProducts += effectiveProduct;
    });

    const revenueTotal = revenueOnline + revenueCash + revenueCard;

    // Cost Calculation (Using calculatedOwnFleetCost if available, otherwise fallback)
    const ownFleetDeliveries = validOrders.filter(o => !!o.driverId).length;

    const totalOwnFleetCalculatedCost = validOrders
        .filter(o => !!o.driverId && o.calculatedOwnFleetCost !== undefined)
        .reduce((sum, o) => sum + (o.calculatedOwnFleetCost || 0), 0);

    const activeDriversCount = drivers.filter(d => d.status !== 'offline').length || 1;
    const totalDailyFixedCost = config.dailyFixedCost * activeDriversCount;

    const totalFleetCost = ownFleetDeliveries > 0 ? totalOwnFleetCalculatedCost : 0;

    const costThirdParty = validOrders
        .filter(o => !o.driverId)
        .reduce((sum, o) => sum + o.costThirdParty, 0);

    const netProfit = revenueTotal - (totalFleetCost + costThirdParty + costIncidents);

    const totalCashReturns = drivers.reduce((sum, d) => {
        return sum + (d.cashReturns?.reduce((s, r) => s + r.amount, 0) || 0);
    }, 0);

    const cashInHandExpected = revenueCash - totalCashReturns;

    const avgCostPerOwnDelivery = ownFleetDeliveries > 0
        ? totalFleetCost / ownFleetDeliveries
        : 0;

    return {
        // Required base fields
        revenue: revenueTotal,
        costs: totalFleetCost + costThirdParty + costIncidents,
        profit: netProfit,
        orderCount: validOrders.length,
        periodStart: Date.now() - 86400000, // Last 24h
        periodEnd: Date.now(),
        // Extended fields
        revenueTotal,
        revenueOnline,
        revenueCard,
        revenueCash,
        grossProducts,
        grossFees,
        costFleetFixed: totalDailyFixedCost,
        costFleetVariable: 0,
        costIdleCapacity: 0,
        costThirdParty,
        costIncidents,
        netProfit,
        cashInHandExpected,
        avgCostPerOwnDelivery
    };
};

export const getDriverSettlement = (driver: Driver, orders: Order[]) => {
    const driverOrders = orders.filter(o => o.driverId === driver.id && (o.status === 'delivered' || o.status === 'archived'));

    const totalProduced = driverOrders.reduce((sum, o) => {
        return sum + (o.orderValue || 0);
    }, 0);

    const cashToCollect = driverOrders.reduce((sum, o) => {
        if (isPaymentCash(o.paymentMethod)) return sum + (o.orderValue || 0);
        return sum;
    }, 0);

    const cardToCollect = driverOrders.reduce((sum, o) => {
        if (isPaymentCard(o.paymentMethod)) return sum + (o.orderValue || 0);
        return sum;
    }, 0);

    const totalSangria = driver.cashReturns?.reduce((sum, r) => sum + r.amount, 0) || 0;

    const netCashDebt = cashToCollect - totalSangria;

    return {
        deliveriesCount: driverOrders.length,
        totalProduced,
        cashToCollect,
        cardToCollect,
        totalSangria,
        netCashDebt
    };
};
